import { useRef } from 'react';
import { useForm } from '@mantine/form';

import { validateReport } from '../../services/reportsApi.js';
import { buildReportPayload } from './reportPayload.js';
import { mapViolations, type Violation } from '../../utils/mapViolations.js';
import { createValuesVersionStore, createWarningsStore } from './formStores.js';

/**
 * Uncontrolled Formular-Hook fuer Report-Formulare (Unfall, Umwelt, Marktdaten).
 *
 * Dieser Hook gibt Werte bewusst nicht mehr per Callback nach oben durch (D-04): der
 * einzige Rueckkanal ist `onValuesChange` innerhalb des Hooks, der ausschliesslich
 * isolierte Abos fuettert (Values-Version-Store, `opts.onValuesChange` als Andockpunkt
 * fuer `useReportCalc`) — nie Parent-State. `handleSave` liest den Snapshot per
 * `form.getValues()`. Geseedet wird von aussen per
 * `form.setInitialValues`/`form.setValues` (D-03).
 *
 * Eine Frontend-Gesamtgueltigkeit berechnet der Hook seit 20-04 (K11/D-08) nicht mehr:
 * Vollstaendigkeit prueft ausschliesslich das Backend beim Einreichen.
 *
 * Der Hook kennt zwei Validierungsstaerken (20-05): bis zum ersten abgelehnten
 * Einreichen-Versuch prueft der Blur nur die Fach-Constraints (unvollstaendige
 * Entwuerfe bleiben unmarkiert), danach zusaetzlich die Pflichtfelder. Der naechste
 * Speicherversuch senkt sie wieder (20-14) -- Speichern misst am Speicher-Massstab.
 *
 * Er kennt seit 20-07 zwei Meldungsstufen (D-09/D-10): dieselbe Antwort traegt
 * blockierende Fehler UND nicht-blockierende Hinweise. Die Hinweise landen in einem
 * eigenen Store, nicht in `form.errors` -- Mantine kennt je Feld nur eine Fehlerliste,
 * und eine Mischung wuerde die rote Stufe entwerten.
 */

export interface UseReportFormOptions<T> {
  /** Wird NACH validate/bump mit den aktuellen Formularwerten aufgerufen (Andockpunkt fuer useReportCalc, D-10). */
  onValuesChange?: (values: T) => void;
  /** Unterdrueckt runValidation (z.B. bei read-only/approved Reports). */
  disabled?: boolean;
  /**
   * Id der bearbeiteten Meldung. Wird an /api/reports/validate durchgereicht, damit der Server
   * ihr Werk nachschlagen und werksabhaengige Constraints genauso beurteilen kann wie beim
   * Einreichen (CSI-Ausnahme, P-01). Ohne sie prueft der Server nur den Payload.
   */
  reportId?: number;
}

export function useReportForm<T extends Record<string, unknown>>(
  kind: string,
  opts?: UseReportFormOptions<T>
) {
  const valuesVersionStore = useRef(createValuesVersionStore()).current;
  const warningsStore = useRef(createWarningsStore()).current;

  const form = useForm<T>({
    mode: 'uncontrolled',
    // Fehler sind ausschliesslich backend-getrieben (runValidation -> setErrors).
    // clearInputErrorOnChange:false verhindert, dass eine Werteaenderung angezeigte
    // Fehler still loescht — sonst wuerde z.B. das Bearbeiten eines anderen Tabs
    // eine gueltige Produktion-Fehlermarkierung wegwischen. Fehler werden erst beim
    // naechsten Blur-runValidation neu gesetzt.
    clearInputErrorOnChange: false,
    // Keine `validate`-Config: alle Feldfehler kommen ausschliesslich vom Backend via setErrors.
    onValuesChange: (values) => {
      valuesVersionStore.bump();
      opts?.onValuesChange?.(values);
    },
  });

  // Inline-Validierung: ein Feld-Blur validiert den gesamten Payload gegen die
  // Backend-Constraints und mapped die Violations auf die passenden Felder.
  const validationSeq = useRef(0);

  // Ab dem ersten fehlgeschlagenen Einreichen-Versuch validiert auch der Blur mit
  // mode='submit' (20-05). Ohne das loescht der naechste Blur die gerade gesetzten
  // Markierungen wieder: die Blur-Validierung setzt die Fehler des GESAMTEN Formulars
  // neu, und ohne Modus meldet ein unvollstaendiger Entwurf korrekterweise nichts.
  // Ein Reiterwechsel per Klick genuegt dafuer schon -- der Fokus verlaesst dabei die
  // Reiterleiste innerhalb des onBlur-Rahmens. Die roten Felder und die
  // Reiter-Kennzeichnung waeren damit sofort wieder weg.
  //
  // Seit 20-14 ist das Flag zweiseitig: `disableSubmitValidation` nimmt es beim naechsten
  // Speicherversuch zurueck. Ohne diese Gegenoperation klebte es bis zum Neuladen der Seite.
  const submitMode = useRef(false);

  /**
   * Der gemeinsame Kern aller Validierungswege: fragt den aktuellen Formularstand ab und
   * ERSETZT Fehler und Hinweise durch das Ergebnis -- aber nur, wenn inzwischen keine
   * neuere Validierung gestartet wurde.
   *
   * `seq` und `mode` werden bewusst vom Aufrufer uebergeben und nicht hier ermittelt: der
   * Blur-Weg vergibt seine Sequenznummer synchron beim Ausloesen und laeuft erst danach
   * deferred los. Wuerde die Nummer erst hier vergeben, koennte eine zwischenzeitliche
   * Modusumschaltung (enableSubmitValidation / disableSubmitValidation) eine bereits
   * eingeplante Blur-Antwort nicht mehr verwerfen.
   */
  function applyValidation(seq: number, mode: 'submit' | undefined): Promise<void> {
    // Die Nutzlast geht durch dieselbe Umformung wie der Speicherpfad (reportPayload.ts) --
    // sonst prueft der Blur einen anderen Stand als den, der gespeichert wuerde.
    return validateReport(
      kind,
      buildReportPayload(kind, form.getValues() as Record<string, unknown>),
      mode,
      opts?.reportId
    )
      .then(({ violations, warnings }) => {
        // Dieselbe Sequenznummern-Pruefung fuer BEIDE Stufen (T-20-07-02): sonst
        // koennte eine ueberholte Antwort die Hinweise einer neueren ueberschreiben,
        // waehrend deren Fehler korrekt stehen bleiben -- die beiden Stufen liefen
        // auseinander.
        if (seq === validationSeq.current) {
          form.setErrors(mapViolations(violations));
          warningsStore.set(mapViolations(warnings));
        }
      })
      .catch(() => {
        // Netzwerk-/Transportfehler werden fuer die Inline-UX still geschluckt.
      });
  }

  function runValidation() {
    if (opts?.disabled) return;
    const seq = ++validationSeq.current;
    const mode = submitMode.current ? 'submit' : undefined;
    // Deferred, damit ein blur-getriggerter Formular-Commit (NumberInput-Formatierung)
    // im Formularstate ankommt, bevor wir es auslesen — sonst validieren wir den
    // Vor-Edit-Wert.
    setTimeout(() => {
      void applyValidation(seq, mode);
    }, 0);
  }

  /**
   * Nach einem abgelehnten Einreichen-Versuch aufzurufen (20-05).
   *
   * Zwei Wirkungen: kuenftige Blur-Validierungen laufen mit mode='submit', und eine
   * noch fliegende Blur-Antwort wird verworfen. Letzteres schliesst ein echtes Rennen:
   * der Klick auf "Einreichen" loest per Fokuswechsel selbst noch eine Blur-Validierung
   * ohne Modus aus; traefe deren Antwort nach den Submit-Markierungen ein, wuerde sie
   * sie ueberschreiben.
   */
  function enableSubmitValidation() {
    submitMode.current = true;
    ++validationSeq.current;
  }

  /**
   * Die Gegenoperation zu `enableSubmitValidation` -- nach dem Speichern aufzurufen.
   *
   * Ohne sie klebt der submit-Modus fuer den Rest der Sitzung: `enableSubmitValidation`
   * hat ihn gesetzt, nichts hat ihn je zurueckgenommen. Jede spaetere Blur-Validierung
   * liefe damit weiter mit `mode='submit'` und wuerde die Pflichtfeld-Markierungen beim
   * naechsten Feldwechsel sofort wieder aufziehen -- das nachtraegliche Aufraeumen durch
   * `revalidateNow()` waere wirkungslos.
   *
   * Der Bump der Sequenznummer verwirft zusaetzlich eine noch fliegende Blur-Antwort aus
   * dem submit-Modus; sie wuerde sonst nach dem Speichern eintreffen und die gerade
   * geraeumten Markierungen zurueckholen.
   */
  function disableSubmitValidation() {
    submitMode.current = false;
    ++validationSeq.current;
  }

  /**
   * Uebernimmt die Hinweise EINES bereits andernorts ausgefuehrten Validierungsaufrufs.
   *
   * Gedacht fuer den Einreichen-Pfad: der ruft `validateReport` selbst auf (er braucht das
   * Ergebnis synchron fuer den Abbruch bzw. die Rueckfrage) und haette seine Hinweise ohne
   * diese Funktion nirgends abzulegen -- der Warnungs-Store wurde bis dahin ausschliesslich
   * vom Blur-Weg gefuellt. Die Felder blieben deshalb ungefaerbt, bis irgendein spaeterer
   * Feldwechsel dieselben Hinweise noch einmal holte.
   *
   * Der Bump der Sequenznummer haelt den Schutz aus `applyValidation` aufrecht, und zwar in
   * BEIDE Richtungen: eine noch fliegende Blur-Antwort wird verworfen und kann diese
   * Hinweise nicht mehr ueberschreiben; eine erst DANACH startende Blur-Validierung
   * bekommt eine hoehere Nummer und ueberschreibt sie regulaer -- so wie sie es auch mit
   * den Feldfehlern des Einreichen-Versuchs tut.
   *
   * Die Fehler derselben Antwort setzt der Aufrufer weiterhin selbst per `form.setErrors`:
   * er unterscheidet zwischen Abbruch (Fehler vorhanden) und Weitergang (keine Fehler) und
   * haengt an diesen Zweig noch die Modusumschaltung und die Meldungstexte.
   */
  function applyWarnings(warnings: Violation[]): void {
    ++validationSeq.current;
    warningsStore.set(mapViolations(warnings));
  }

  /**
   * Validiert sofort (nicht deferred) im aktuell gueltigen Modus und ersetzt Feldfehler
   * UND Hinweise durch das Ergebnis. Gedacht fuer den Speichern-Pfad, der die Markierungen
   * eines vorherigen Einreichen-Versuchs durch den Speicher-Massstab ersetzen soll:
   * fehlende Pflichtfelder verschwinden (sie blockieren das Speichern nicht), fachlich
   * falsche Werte bleiben stehen (sie melden auch ohne Modus).
   *
   * Bewusst OHNE `opts.disabled`-Pruefung: das Flag bremst die Blur-Automatik, waehrend
   * eine Operation laeuft -- und genau waehrend dieser Operation ruft der Speichern-Pfad
   * hier herein. Der Aufruf ist explizit und an eine bewusste Nutzeraktion gebunden.
   *
   * Laeuft im selben Sequenzschutz wie die Blur-Validierung: die frisch vergebene Nummer
   * verwirft aeltere fliegende Antworten, und ein danach startender Blur verwirft
   * umgekehrt diese hier.
   */
  function revalidateNow(): Promise<void> {
    const seq = ++validationSeq.current;
    const mode = submitMode.current ? 'submit' : undefined;
    return applyValidation(seq, mode);
  }

  return {
    form,
    runValidation,
    enableSubmitValidation,
    disableSubmitValidation,
    applyWarnings,
    revalidateNow,
    valuesVersionStore,
    warningsStore,
  };
}
